import { For, createSignal, Show } from "solid-js";

type ChatTurn = { role: "user" | "assistant"; text: string };

export function TEDWisePanel() {
  const [history, setHistory] = createSignal<ChatTurn[]>([]);
  const [draft, setDraft] = createSignal("");
  const [busy, setBusy] = createSignal(false);

  async function send() {
    const q = draft().trim();
    if (!q || busy()) return;
    setHistory((h) => [...h, { role: "user", text: q }]);
    setDraft("");
    setBusy(true);
    try {
      const res = await fetch(`${import.meta.env.VITE_HOUSTON_API_URL ?? ""}/api/tedwise/chat`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: q }),
      });
      const data = (await res.json()) as { reply: string };
      setHistory((h) => [...h, { role: "assistant", text: data.reply }]);
    } catch {
      setHistory((h) => [...h, { role: "assistant", text: "[error contacting TEDWise]" }]);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div class="panel-bezel flex items-stretch overflow-hidden" data-panel>
      {/* Label */}
      <div class="flex items-center border-r border-[rgba(255,255,255,0.04)] px-4">
        <div class="flex items-center gap-2">
          <div class="h-1.5 w-1.5 rounded-full bg-[var(--color-accent)] animate-beacon" />
          <span class="font-display text-[9px] font-bold uppercase tracking-[0.2em] text-[var(--color-accent)]">
            TEDWise
          </span>
        </div>
      </div>

      {/* History scroll */}
      <Show when={history().length > 0}>
        <div class="flex items-center gap-3 overflow-x-auto border-r border-[rgba(255,255,255,0.04)] px-3 py-2 max-w-[50%]">
          <For each={history().slice(-4)}>
            {(t) => (
              <span
                class={`whitespace-nowrap font-mono text-[10px] ${
                  t.role === "user"
                    ? "text-[var(--color-info)]"
                    : "text-[var(--color-text-secondary)]"
                }`}
              >
                <span class="opacity-40">{t.role === "user" ? ">" : "←"}</span>{" "}
                {t.text.length > 80 ? t.text.slice(0, 77) + "…" : t.text}
              </span>
            )}
          </For>
        </div>
      </Show>

      {/* Input */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          send();
        }}
        class="flex flex-1 items-center"
      >
        <span class="pl-3 font-mono text-[10px] text-[var(--color-accent)] opacity-60">
          {busy() ? "⟳" : ">"}
        </span>
        <input
          class="flex-1 bg-transparent px-2 py-2.5 font-mono text-[11px] text-[var(--color-text-primary)] outline-none placeholder:text-[var(--color-text-dim)]"
          placeholder="Ask TEDWise about ted.com…"
          value={draft()}
          onInput={(e) => setDraft(e.currentTarget.value)}
          disabled={busy()}
        />
        <button
          type="submit"
          class="mr-2 rounded border border-[var(--color-border-panel)] px-3 py-1 font-mono text-[9px] uppercase tracking-wider text-[var(--color-text-secondary)] transition-all hover:border-[var(--color-accent)] hover:text-[var(--color-accent)] disabled:opacity-30"
          disabled={busy()}
        >
          Send
        </button>
      </form>
    </div>
  );
}
